<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { expanded: !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- HEADER -->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <li class="nav-item mr-auto ml-auto">
            <b-link class="navbar-brand" to="/">
              <span class="brand-logo mt-1">
                <b-img :src="appLogoImage" alt="logo" style="max-width: 100px" />
              </span>
            </b-link>
          </li>

          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>

    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="filteredNavMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
  </div>
</template>

<script>
import fullNavMenu from "@/navigation/vertical/index.js";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { BLink, BImg } from "bootstrap-vue";
import { provide, computed, ref, onMounted, onUnmounted } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { $themeConfig } from "@themeConfig";
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue";
import useVerticalNavMenu from "./useVerticalNavMenu";

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },

  props: {
    isVerticalMenuActive: { type: Boolean, required: true },
    toggleVerticalMenuActive: { type: Function, required: true },
  },

  setup(props) {
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();
    const shallShadowBottom = ref(false);
    provide("isMouseHovered", isMouseHovered);

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    );

    const { appLogoImage } = $themeConfig.app;

    // ---------------------------
    // ROLE HANDLING
    // ---------------------------
    const role = ref(localStorage.getItem("role") || "");

    const updateRole = () => {
      role.value = localStorage.getItem("role") || "";
    };

    onMounted(() => {
      window.addEventListener("role-changed", updateRole);
    });

    onUnmounted(() => {
      window.removeEventListener("role-changed", updateRole);
    });

    // ---------------------------
    // RBAC FILTER
    // ---------------------------
    const filteredNavMenuItems = computed(() => {
      if (!role.value) return [];

      const alwaysVisible = ["Change password"];

      const adminRestricted = [
        "Packages",
        "Advertisement",
        "Terms & Conditions",
        "Privacy Policy",
      ];

      const memberRestricted = [
        "Push Notifications",
        "Talent (Under 18)",
        "Packages",
        "Advertisement",
        "Reports",
        "Analysis Reports",
        "Referrals",
        "Terms & Conditions",
        "Privacy Policy",
      ];

      const coordinatorScreens = [
        "Dashboard",
        "Teams",
        "Talents",
        "Talent Scouts",
        "Talents (Under 18)",
        "Geo Verse",
        "Geo Nexus",
        "Posts",
        "Reports",
        ...alwaysVisible,
      ];

      return fullNavMenu
        .map(item => {
          if (item.children && item.children.length) {
            let children = [];

            if (role.value === "super-admin") {
              children = item.children;
            } else if (role.value === "admin") {
              children = item.children.filter(
                c => !adminRestricted.includes(c.title) || alwaysVisible.includes(c.title)
              );
            } else if (role.value === "member") {
              children = item.children.filter(
                c => !memberRestricted.includes(c.title) || alwaysVisible.includes(c.title)
              );
            } else if (role.value === "coordinator") {
              children = item.children.filter(
                c => coordinatorScreens.includes(c.title)
              );
            }

            return children.length ? { ...item, children } : null;
          }

          if (role.value === "super-admin") return item;
          if (role.value === "admin")
            return adminRestricted.includes(item.title) && !alwaysVisible.includes(item.title)
              ? null
              : item;

          if (role.value === "member")
            return memberRestricted.includes(item.title) && !alwaysVisible.includes(item.title)
              ? null
              : item;

          if (role.value === "coordinator")
            return coordinatorScreens.includes(item.title) ? item : null;

          return null;
        })
        .filter(Boolean);
    });

    return {
      filteredNavMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,
      shallShadowBottom,
      skin,
      appLogoImage,
    };
  },
};
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>

<style scoped>
.navbar-header {
  height: 8rem;
}
</style>